import { join } from 'node:path';
import type { VariantOf } from 'variant';
import { variant, fields, isType } from 'variant';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { CloudProvider } from '@wix/backend-as-a-service-client';
import { validateDirectory, validatePackageName } from './validations/index.js';
import type { TemplateParams } from './validations/validate-template-params.js';
import type { CliHeadlessTemplate } from './components/ChooseTemplate.js';

export interface CommandOptions {
  siteTemplate?: string;
  templateRepo?: string;
  templateRepoPath?: string;
  templatePath?: string;
  skipInstall?: boolean;
  skipGit?: boolean;
  templateParams?: TemplateParams;
  businessName?: string;
  folderName?: string;
  publish?: boolean;
}

export const DEFAULT_HEADLESS_TEMPLATE_NAME = 'blank';

export const pureHeadlessTemplates: CliHeadlessTemplate[] = [
  {
    name: 'commerce',
    title: 'Commerce (Wix Stores)',
    subtitle: 'Product catalog, checkout, and more',
    siteTemplateId: 'e5da13f4-c01e-4b61-a9c7-55dacd961d54',
    gitPath: 'astro/commerce',
    vibeCompatible: false,
  },
  {
    name: 'scheduler',
    title: 'Scheduler (Wix Bookings)',
    subtitle: 'Appointment booking and calendar',
    siteTemplateId: '72ade0e3-1871-4c04-ac54-419ca874d9d3',
    gitPath: 'astro/scheduler',
    vibeCompatible: false,
  },
  {
    name: 'registration',
    title: 'Registration (Wix Forms)',
    subtitle: 'Inputs, dropdowns and field validations',
    siteTemplateId: 'e5d63bf1-cd06-48eb-ad77-0da9235adcf1',
    gitPath: 'astro/registration',
    vibeCompatible: false,
  },
  {
    name: 'blank',
    title: 'Blank',
    subtitle: 'Basic Astro project',
    siteTemplateId: '212b41cb-0da6-4401-9c72-7c579e6477a2',
    gitPath: 'astro/blank',
    vibeCompatible: false,
  },
];

// TODO: For now we do not want to expose the Vibe Compatible templates.
// export const vibeCompatibleTemplates: CliHeadlessTemplate[] = [
//   {
//     title: 'Blank',
//     subtitle: 'A minimal website that displays "Hello"',
//     siteTemplateId: 'a59b41a5-a5db-4d7c-804f-5dbc86e04c3e',
//     gitPath: 'astro/hello',
//     vibeCompatible: true,
//   },
//   {
//     title: 'Stores',
//     subtitle: 'Stores Astro project',
//     siteTemplateId: '38fee0f9-830e-445c-8eec-464455c889b1',
//     gitPath: 'astro/store',
//     vibeCompatible: true,
//   },
//   {
//     title: 'CMS',
//     subtitle:
//       'Wix CMS template for building directory or catalog-style site Product catalog, checkout, and more',
//     siteTemplateId: 'a164bf71-a82e-43b4-a5c9-a037e4345df1',
//     gitPath: 'astro/cms',
//     vibeCompatible: true,
//   },
// ];

const headlessTemplates: CliHeadlessTemplate[] = [...pureHeadlessTemplates];

export const TemplateSource = variant({
  HeadlessApp: fields<CliHeadlessTemplate>(),
  GitRepo: fields<{ siteTemplateId: string; url: string; path: string }>(),
  Local: fields<{ siteTemplateId: string; path: string }>(),
});

export type TemplateSource = VariantOf<typeof TemplateSource>;

function parseProviderString(cloudProvider: string): CloudProvider {
  switch (cloudProvider.trim().toLowerCase()) {
    case 'cloudflare':
      return CloudProvider.CLOUD_FLARE;
    case 'kubernetes':
      return CloudProvider.KUBERNETES;
    default:
      throw new CliError({
        code: CliErrorCode.HeadlessArgumentIsInvalid({
          errorMessage: `Invalid cloud provider: "${cloudProvider}".`,
        }),
        cause: null,
      });
  }
}

function parseCloudProviders(): CloudProvider[] {
  const envProviders = process.env.WIX_CLOUD_PROVIDERS;

  if (envProviders) {
    const providers = envProviders
      .split(',')
      .map((provider) => provider.trim())
      .filter(Boolean);
    return providers.map(parseProviderString);
  }

  return [CloudProvider.CLOUD_FLARE];
}

async function parseTemplateOptions(options: CommandOptions) {
  if (options.siteTemplate) {
    const appTemplate = headlessTemplates.find(
      (item) => item.name === options.siteTemplate
    );

    if (!appTemplate) {
      throw new CliError({
        code: CliErrorCode.CliAppTemplateNotFound({
          id: options.siteTemplate,
        }),
        cause: null,
      });
    }

    if (options.templateRepo && options.templateRepoPath) {
      return TemplateSource.GitRepo({
        siteTemplateId: appTemplate.siteTemplateId,
        url: options.templateRepo,
        path: options.templateRepoPath,
      });
    }

    if (options.templatePath) {
      return TemplateSource.Local({
        siteTemplateId: appTemplate.siteTemplateId,
        path: options.templatePath,
      });
    }

    return TemplateSource.HeadlessApp(appTemplate);
  }
}

async function parseFolderNameOptions(
  targetParentFolder: string,
  options: CommandOptions
) {
  const folderName = options.folderName;
  if (folderName) {
    if (!(await validateDirectory(join(targetParentFolder, folderName)))) {
      throw new CliError({
        code: CliErrorCode.HeadlessArgumentIsInvalid({
          errorMessage: `"${folderName}" directory is not empty`,
        }),
        cause: undefined,
      });
    }

    const npmValidationErrors = await validatePackageName(folderName);
    if (isType(npmValidationErrors, 'Error')) {
      throw new CliError({
        code: CliErrorCode.HeadlessArgumentIsInvalid({
          errorMessage: `Invalid package name "${folderName}". Fix the following issues: ${npmValidationErrors.payload.join(', ')}`,
        }),
        cause: undefined,
      });
    }

    return folderName;
  }

  return;
}

const processPublish = ({
  template,
  folderName,
  businessName,
  publish,
}: {
  template?: TemplateSource;
  folderName?: string;
  businessName?: string;
  publish?: boolean;
}) => {
  if (template && folderName && businessName) {
    return publish ?? false;
  }
  return publish;
};

export async function parseCommandOptions(
  targetParentFolder: string,
  options: CommandOptions
) {
  const template = await parseTemplateOptions(options);
  const cloudProviders = parseCloudProviders();
  const folderName = await parseFolderNameOptions(targetParentFolder, options);

  const publish = processPublish({
    template,
    folderName,
    businessName: options.businessName,
    publish: options.publish,
  });

  return {
    template,
    folderName,
    businessName: options.businessName,
    cloudProviders,
    publish,
  };
}
